<template>
    <div id="serviceModal" ref="serviceModal" class="modal fade" data-bs-backdrop="static" data-bs-keyboard="false"
        tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-md">
            <div class="modal-content">
                <div class="modal-body">
                    <div class="flex flex-col gap-[40px]">
                        <div class="flex items-center justify-between rounded-[8px] p-[8px] bg-[#092e87]">
                            <h5 class="mb-0 font-roboto font-bold text-[16px] text-[#fff]">
                                {{ isEditing ? "Edit Service Item" : "Add Service Item" }}
                            </h5>
                            <button v-if="!isEditing" type="button" @click="newServiceClicked()"
                                title="Add New Service Item" aria-label="Add New Service Item" class="shrink-0">
                                <img src="../../assets/images/plus-orange-circle-icon.svg" alt="Add New Service Item"
                                    class="w-[30px] h-[30px]">
                            </button>
                        </div>

                        <div class="p-[5px] flex flex-col gap-[30px] max-h-[70vh] overflow-auto customScrollbar">
                            <div v-for="(service, index) in serviceForms" :key="service.id"
                                class="relative flex flex-col gap-[15px] p-[10px] rounded-[10px] shadow-[0px_0px_10px_0px_#00000026]">
                                <!-- Type -->
                                <div class="form-group">
                                    <label class="mb-2">Type</label>

                                    <div class="flex items-center gap-[12px]">
                                        <!-- Recommended -->
                                        <label class="flex items-center gap-[5px] cursor-pointer">
                                            <input v-model="service.type" @change="typeChanged(service)"
                                                type="radio" :name="'type-' + service.id" value="recommended"
                                                class="hidden" />

                                            <span
                                                class="w-[25px] h-[25px] rounded-full flex items-center justify-center bg-[#FFB000]">
                                                <svg v-if="service.type === 'recommended'"
                                                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                    class="w-[17px] h-[17px] fill-white">
                                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19
                            21 7l-1.41-1.41z" />
                                                </svg>
                                            </span>

                                            <span class="text-[14px] text-[#333]">
                                                Recommended
                                            </span>
                                        </label>

                                        <!-- Required -->
                                        <label class="flex items-center gap-[5px] cursor-pointer">
                                            <input v-model="service.type" @change="typeChanged(service)"
                                                type="radio" :name="'type-' + service.id" value="required"
                                                class="hidden" />

                                            <span
                                                class="w-[25px] h-[25px] rounded-full flex items-center justify-center bg-[#FF6464]">
                                                <svg v-if="service.type === 'required'"
                                                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                    class="w-[17px] h-[17px] fill-white">
                                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19
                            21 7l-1.41-1.41z" />
                                                </svg>
                                            </span>

                                            <span class="text-[14px] text-[#333]">
                                                Required
                                            </span>
                                        </label>
                                    </div>

                                    <small v-if="service.errors.type" class="text-danger">
                                        {{ service.errors.type }}
                                    </small>
                                </div>

                                <!-- Service Name -->
                                <div class="form-group">
                                    <label class="mb-2">
                                        Component Name
                                    </label>

                                    <input v-model="service.serviceName" @input="clearError(service, 'serviceName')"
                                        type="text" class="form-control"
                                        :class="{ 'border-danger': service.errors.serviceName }"
                                        placeholder="Enter Component Name">

                                    <small v-if="service.errors.serviceName" class="text-danger">
                                        {{ service.errors.serviceName }}
                                    </small>
                                </div>

                                <!-- Select Service -->
                                <div class="flex flex-col gap-[10px]">
                                    <div class="form-group">
                                        <label class="mb-1">
                                            Select Service
                                        </label>

                                        <select v-model="service.selectedService"
                                            @change="serviceChanged(service)" class="form-select"
                                            :class="{ 'border-danger': service.errors.selectedService }"
                                            :disabled="!service.type">
                                            <option value="" disabled>
                                                Select Service
                                            </option>

                                            <option v-for="action in getBookingActions(service.type)"
                                                :key="action.id" :value="action">
                                                {{ action.new_display_name }}
                                            </option>
                                        </select>

                                        <small v-if="service.errors.selectedService" class="text-danger">
                                            {{ service.errors.selectedService }}
                                        </small>
                                    </div>
                                </div>

                                <!-- Labor Fee -->
                                <div class="form-group">
                                    <div class="flex items-center justify-between mb-2">
                                        <label class="mb-0">
                                            Labor Fee
                                        </label>

                                        <!-- Labor Warranty -->
                                        <label :for="'laborWarranty-' + service.id"
                                            class="mb-0 flex items-center gap-[8px] select-none cursor-pointer text-[#092e87]">
                                            Labor Warranty
                                            <input :id="'laborWarranty-' + service.id" v-model="service.laborWarranty"
                                                @change="laborWarrantyChanged(service)" type="checkbox"
                                                class="sr-only peer">
                                            <span
                                                class="relative w-[36px] h-[20px] rounded-full bg-[#CCCCCC] transition-colors peer-checked:bg-[#092e87] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:w-[16px] after:h-[16px] after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-[16px]"></span>
                                        </label>
                                    </div>

                                    <input v-model="service.laborFee" @input="laborFeeChanged(service)"
                                        :disabled="service.laborWarranty" type="text"
                                        inputmode="decimal"
                                        class="form-control disabled:cursor-not-allowed" placeholder="$0.00">

                                    <small v-if="service.errors.laborFee" class="text-danger">
                                        {{ service.errors.laborFee }}
                                    </small>
                                </div>

                                <!-- Remove -->
                                <button v-if="serviceForms.length > 1" type="button" @click="removeService(index)"
                                    class="absolute top-[15px] right-[15px] w-[35px] h-[35px] rounded-full flex items-center justify-center bg-[#fe5000] text-[#fff]">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                        class="w-[20px] h-[20px]">
                                        <path d="M0 0h24v24H0z" fill="none" />
                                        <path fill="currentColor" fill-rule="evenodd"
                                            d="M9.774 5L3.758 3.94l.174-.986a.5.5 0 0 1 .58-.405L18.411 5h.088h-.087l1.855.327a.5.5 0 0 1 .406.58l-.174.984l-2.09-.368l-.8 13.594A2 2 0 0 1 15.615 22H8.386a2 2 0 0 1-1.997-1.883L5.59 6.5h12.69H5.5zM9 9l.5 9H11l-.4-9zm4.5 0l-.5 9h1.5l.5-9zm-2.646-7.871l3.94.694a.5.5 0 0 1 .405.58l-.174.984l-4.924-.868l.174-.985a.5.5 0 0 1 .58-.405z" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="flex items-center justify-center gap-[10px]">
                            <button @click="hide()"
                                class="rounded-[8px] px-[20px] py-[7px] font-roboto text-[16px] font-normal bg-[#FE5000] text-[#fff]">
                                Cancel
                            </button>
                            <button :disabled="!isAllServicesValid" @click="submitClicked()" :class="[
                                'rounded-[8px] px-[20px] py-[7px] font-roboto text-[16px] font-normal text-[#fff]',
                                isAllServicesValid
                                    ? 'bg-[#092E87] cursor-pointer'
                                    : 'bg-[#999] cursor-not-allowed'
                            ]">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Modal } from "bootstrap";

let formId = 0;

function emptyServiceForm() {
    return {
        id: ++formId,
        type: '',
        serviceName: '',
        selectedService: '',
        laborWarranty: false,
        laborFee: '',
        errors: {}
    };
}

export default {
    components: {
    },

    data() {
        return {
            modal: null,
            serviceForms: [emptyServiceForm()],
            // Index of the service being edited on the summary page, null when adding
            editIndex: null
        };
    },

    emits: ["save"],

    computed: {
        ...mapGetters(["bookingServices"]),

        isEditing() {
            return this.editIndex !== null;
        },

        isAllServicesValid() {
            return this.serviceForms.every(service => {
                const typeValid = !!service.type;

                const serviceNameValid =
                    service.serviceName &&
                    service.serviceName.trim() !== '';

                const selectedServiceValid =
                    !!service.selectedService;

                // Either Labor Warranty OR Labor Fee
                const laborValid =
                    service.laborWarranty ||
                    (service.laborFee && service.laborFee.trim() !== '');

                return (
                    typeValid &&
                    serviceNameValid &&
                    selectedServiceValid &&
                    laborValid
                );
            });
        }
    },

    mounted() {
    },

    watch: {
    },

    methods: {

        show(service = null, index = null) {
            this.editIndex = service ? index : null;
            this.serviceForms = [service ? this.formFromService(service) : emptyServiceForm()];

            if (this.modal == null) {
                this.modal = new Modal(this.$refs.serviceModal);

                this.$refs.serviceModal.addEventListener("hidden.bs.modal", () => {
                    document.documentElement.style.overflow = "";
                });
            }

            this.modal.show();
        },

        hide() {
            if (this.modal == null) {
                return;
            }

            this.modal.hide();
            this.resetForms();
        },

        resetForms() {
            this.editIndex = null;
            this.serviceForms = [emptyServiceForm()];
        },

        formFromService(service) {
            const type = service.value == 3 ? 'required' : 'recommended';
            // Match against the current list so the select shows the saved service
            const selectedService = this.getBookingActions(type)
                .find(action => action.id == service.booking_action_id) ?? '';

            return {
                ...emptyServiceForm(),
                type,
                serviceName: service.option ?? '',
                selectedService,
                laborWarranty: !!service.is_service_warranty,
                laborFee: service.is_service_warranty || service.diagnosis_labor_price == null
                    ? ''
                    : String(service.diagnosis_labor_price),
            };
        },

        getBookingActions(type) {
            const actions = this.bookingServices?.actions;
            if (!actions || !type) {
                return [];
            }
            return actions[type] ?? [];
        },

        typeChanged(service) {
            this.clearError(service, 'type');
            // Services differ per type, so drop a selection from the other list
            service.selectedService = '';
        },

        serviceChanged(service) {
            this.clearError(service, 'selectedService');
            // Labor Fee defaults to the selected service's price
            if (!service.laborWarranty && service.selectedService?.price != null) {
                service.laborFee = String(service.selectedService.price);
                this.clearError(service, 'laborFee');
            }
        },

        clearError(service, field) {
            if (service.errors[field]) {
                service.errors[field] = '';
            }
        },

        laborWarrantyChanged(service) {
            // Warranty checked means Labor Fee requirement is satisfied
            if (service.laborWarranty) {
                service.laborFee = '';
                service.errors.laborFee = '';
            } else if (service.selectedService?.price != null) {
                service.laborFee = String(service.selectedService.price);
            }

            // If warranty is unchecked and there is no labor fee,
            // don't immediately show an error.
            // Error will be shown when Add New Service is clicked.
        },

        laborFeeChanged(service) {
            // Digits with a single dot and at most 2 decimals
            const [integer, ...rest] = service.laborFee.replace(/[^0-9.]/g, '').split('.');
            service.laborFee = rest.length ? `${integer}.${rest.join('').slice(0, 2)}` : integer;

            // If user starts entering Labor Fee,
            // remove Labor Fee error
            if (service.laborFee) {
                service.errors.laborFee = '';
            }
        },

        newServiceClicked() {
            // Only validate the FIRST card
            const service = this.serviceForms[0];

            service.errors = {};

            // Type
            if (!service.type) {
                service.errors.type = 'Please select type';
            }

            // Service Name
            if (!service.serviceName.trim()) {
                service.errors.serviceName = 'Service name is required';
            }

            // Select Service
            if (!service.selectedService) {
                service.errors.selectedService = 'Please select a service';
            }

            // Labor Warranty OR Labor Fee
            if (
                !service.laborWarranty &&
                !service.laborFee
            ) {
                service.errors.laborFee =
                    'Please select Labor Warranty or enter Labor Fee';
            }

            // Stop here if first card has errors
            if (Object.keys(service.errors).length > 0) {
                return;
            }

            // First card is valid -> add new card at TOP
            this.serviceForms.unshift(emptyServiceForm());
        },

        removeService(index) {
            this.serviceForms.splice(index, 1);
        },

        submitClicked() {
            if (!this.isAllServicesValid) {
                return;
            }

            // Same shape as the diagnostic options on the summary page
            const services = this.serviceForms.map(service => {
                const data = {
                    diagnostic_option_id: null,
                    option: service.serviceName.trim(),
                    value: service.type === 'required' ? 3 : 2,
                    selected_service: service.selectedService,
                    booking_action_id: service.selectedService.id,
                    is_service_warranty: service.laborWarranty,
                    parts: [],
                    is_error: false,
                    error: ''
                };
                // No labor price when the labor is under warranty
                if (!service.laborWarranty) {
                    data.diagnosis_labor_price = Number(service.laborFee);
                }
                return data;
            });

            this.$emit("save", services, this.editIndex);
            this.modal.hide();
            this.resetForms();
        },

    }
}
</script>